/**
 * ResultsMatrix — per-case payloads for one run.
 *
 * Cases are optional (a run may be summary-only). Each row shows the
 * input preview, the model's response, the per-case score, and any
 * extra verdicts from the pushing pipeline (`scores` JSONB, rendered
 * verbatim). A case's trace id links to `?trace=<id>` on the run page,
 * which renders the full multi-turn trajectory (TranscriptEvents, same
 * as the benchmarks pages) as its own view.
 */

import { Link } from 'react-router-dom'

import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatScore } from '@/components/evals/format'
import type { EvalCaseResponse } from '@/api/generated/model'

function ExtraScores({ scores }: { scores: EvalCaseResponse['scores'] }): React.JSX.Element {
  const entries = Object.entries(scores)
  if (entries.length === 0) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex flex-col gap-0.5">
      {entries.map(([key, value]) => (
        <span key={key} className="font-mono text-xs text-muted-foreground">
          {key}: {JSON.stringify(value)}
        </span>
      ))}
    </div>
  )
}

export function ResultsMatrix({ cases }: { cases: EvalCaseResponse[] }): React.JSX.Element {
  if (cases.length === 0) {
    return (
      <EmptyState
        title="No per-case results reported"
        description="This run was pushed summary-only, so just its headline score and metrics are available."
      />
    )
  }

  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Input</TableHead>
            <TableHead>Response</TableHead>
            <TableHead className="text-right">Score</TableHead>
            <TableHead>Verdicts</TableHead>
            <TableHead>Trace</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cases.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="max-w-64 whitespace-pre-wrap break-words align-top text-xs">
                {c.input_preview || <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="max-w-96 whitespace-pre-wrap break-words align-top text-xs">
                {c.response_text || <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="align-top text-right text-sm tabular-nums">
                {formatScore(c.score)}
              </TableCell>
              <TableCell className="align-top">
                <ExtraScores scores={c.scores} />
              </TableCell>
              <TableCell className="align-top">
                {c.trace_id ? (
                  <Link
                    to={{ search: `trace=${c.trace_id}` }}
                    className="break-all font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
                  >
                    {c.trace_id}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
